/**
 * The host's access sheet (ADR-034) — opened when an app calls the
 * `app-access-dialog` client-side entity ("Manage access" / "Request
 * access"). It shows the person their own access to the app's data in the
 * workspace they are acting in, the reasons, and a request form — the same
 * "My access" section as the Access screen — and, for an access manager or
 * reviewer, a link to the full Access screen in a new tab so the app stays
 * open.
 *
 * Every word is the platform's (role and permission labels are the app's own
 * text, attributed to it), and every change is a host-session call to the
 * Access routes with their own gates. The app supplies nothing and learns only
 * whether the person's own access may have changed.
 */

import { useRef } from 'react';
import { ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { MyAppAccessDto } from '@/lib/api';
import type { AppAccessDialogResult } from '@fias/client-entities';
import { useMyAppAccess } from '@/auth/hooks/useAppAccess';
import { MyAccessSection } from '@/auth/pages/app-access/MyAccessSection';
import { appAccessSectionPath, describeAppAccessError } from '@/auth/pages/app-access/shared';

interface AppAccessSheetProps {
  open: boolean;
  archeId: string;
  holderId: string;
  archeName: string;
  onResolve: (result: AppAccessDialogResult) => void;
}

/** What the app could notice: the access the person holds, and their open request. */
function fingerprint(mine: MyAppAccessDto | undefined): string {
  if (!mine) return '';
  return JSON.stringify([
    mine.mode,
    mine.tier,
    mine.permissions,
    mine.roles.map((r) => [r.roleId, r.eligible, r.expiresAt]),
  ]);
}

export function AppAccessSheet({
  open,
  archeId,
  holderId,
  archeName,
  onResolve,
}: AppAccessSheetProps) {
  const scope = { holderId, archeId };
  const mine = useMyAppAccess(scope);
  // The answer the sheet opened on; closing compares the latest against it.
  const initial = useRef<string | null>(null);
  if (initial.current === null && mine.data) initial.current = fingerprint(mine.data);

  async function close() {
    const latest = await mine.refetch();
    onResolve({
      changed: initial.current !== null && fingerprint(latest.data) !== initial.current,
    });
  }

  const appName = mine.data?.appName ?? archeName;
  const governs = !!mine.data && (mine.data.seat.manager || mine.data.seat.reviewer);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && void close()}>
      <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col">
        <DialogHeader>
          <DialogTitle>Access to {appName}</DialogTitle>
          <DialogDescription>
            Shown by Fias. What you can do with {appName}’s data in this workspace, and asking for
            more.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {mine.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : mine.isError || !mine.data ? (
            <p className="text-sm text-destructive">
              {describeAppAccessError(mine.error, 'Your access could not be loaded.')}
            </p>
          ) : (
            <MyAccessSection scope={scope} appName={appName} mine={mine.data} />
          )}
        </DialogBody>
        <DialogFooter className="gap-2">
          {governs && (
            <Button variant="outline" asChild>
              <a
                href={appAccessSectionPath(holderId, archeId, 'people')}
                target="_blank"
                rel="noopener noreferrer"
              >
                Manage people and roles <ExternalLink className="ml-1 h-4 w-4" aria-hidden="true" />
              </a>
            </Button>
          )}
          <Button onClick={() => void close()}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
